import { getAccessToken, logout } from './auth';

/** Servicepartner mit offenen Versänden — der einzige mögliche Empfänger eines Lieferscheins. */
export interface DeliverySlipRecipient {
  id:           number;
  description:  string;
  device_count: number;
}

/** Ein Gerät, das mit dem nächsten Lieferschein hinausgeht. */
export interface DeliverySlipCandidate {
  shipment_id:       number | null;
  stock_device_id:   number;
  stock_number:      string;
  vendor:            string;
  master:            string;
  color:             string;
  memory_size_label: string;
  condition:         string;
  /** Hersteller, Gerät, Farbe und Speicher in einer Zeile — wie auf dem PDF (dort ohne Zustand). */
  description:       string;
  imei_sn:           string;
  error_pattern_id:  number | null;
  error_pattern:     string;
  /** Preis des Fehlerbilds für Master × Servicepartner (Aufschlag der Rezeptur, netto). */
  error_pattern_price:       number | null;
  /** Derselbe Preis formatiert, z.B. „50,00 €"; leer, wenn keiner hinterlegt ist. */
  error_pattern_price_label: string;
}

/**
 * Die Richtung eines Lieferscheins. Beide liegen in derselben Tabelle und
 * derselben Nummernreihe — die Nummer allein sagt nicht, wohin das Paket ging.
 */
export type SlipDirection = 'to_partner' | 'from_partner';

/** Ein erstellter Lieferschein in der Übersicht — hinaus oder zurück. */
export interface DeliverySlip {
  id:                 number;
  slip_number:        string;
  direction:          SlipDirection;
  /** Bei einem Lieferschein hinaus wir, bei einer Rücksendung der Partner. */
  sender:             string;
  recipient:          string;
  servicepartner:     { id: number; description: string };
  creation_timestamp: string;
  created_by:         string;
  device_count:       number;
  /**
   * Wie viele Positionen am anderen Ende angekommen sind: hinaus die, die der
   * Partner im Portal übernommen hat, zurück die, die bei uns angenommen wurden.
   */
  arrived_count:      number;
}

export interface DeliverySlipResult {
  id:           number;
  /** Anzeigenummer aus dem Kreis RL-8000. */
  slip_number:  string;
  device_count: number;
  /** false = Lieferschein steht, nur das Papier fehlt (siehe `print_warning`). */
  printed:      boolean;
  print_warning: string | null;
}

interface ApiEnvelope<T> {
  result: 'SUCCESS' | 'UNAUTHORIZED' | 'FORBIDDEN' | 'NOT_FOUND' | 'BAD_REQUEST' | 'INTERNAL_ERROR' | 'METHOD_NOT_ALLOWED' | 'CONFLICT';
  reason?: string;
  fields?: string[];
  items?: T[];
  item?:  T;
}

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code: ApiEnvelope<unknown>['result'] | 'network' | 'parse',
  ) {
    super(message);
  }
}

const BASE = `${window.location.origin}/api/delivery-slips`;

async function request<T>(path: string, init: RequestInit = {}): Promise<ApiEnvelope<T>> {
  const token = await getAccessToken();
  if (!token) {
    throw new ApiError('Nicht angemeldet.', 401, 'UNAUTHORIZED');
  }

  let resp: Response;
  try {
    resp = await fetch(path, {
      ...init,
      headers: {
        'Accept':        'application/json',
        'Content-Type':  'application/json',
        'Authorization': `Bearer ${token}`,
        ...(init.headers ?? {}),
      },
    });
  } catch (e) {
    throw new ApiError(`Netzwerkfehler: ${(e as Error).message}`, 0, 'network');
  }

  if (resp.status === 401) {
    logout('none');
  }

  let body: ApiEnvelope<T>;
  try {
    body = (await resp.json()) as ApiEnvelope<T>;
  } catch {
    throw new ApiError(`Unlesbare Server-Antwort (${resp.status})`, resp.status, 'parse');
  }

  if (!resp.ok || body.result !== 'SUCCESS') {
    throw new ApiError(body.reason ?? `Server-Fehler ${resp.status}`, resp.status, body.result);
  }

  return body;
}

export async function list(): Promise<DeliverySlip[]> {
  const body = await request<DeliverySlip>(BASE, { method: 'GET' });
  return body.items ?? [];
}

/** Die Geräte auf einem Lieferschein — dieselbe Quelle wie das PDF. */
export async function listSlipDevices(slipId: number): Promise<DeliverySlipCandidate[]> {
  const body = await request<DeliverySlipCandidate>(`${BASE}/${slipId}/devices`, { method: 'GET' });
  return body.items ?? [];
}

export async function listRecipients(): Promise<DeliverySlipRecipient[]> {
  const body = await request<DeliverySlipRecipient>(`${BASE}/recipients`, { method: 'GET' });
  return body.items ?? [];
}

export async function listCandidates(servicepartnerLocationId: number): Promise<DeliverySlipCandidate[]> {
  const body = await request<DeliverySlipCandidate>(
    `${BASE}/candidates?servicepartner_location_id=${servicepartnerLocationId}`,
    { method: 'GET' },
  );
  return body.items ?? [];
}

/**
 * Erstellt den Lieferschein und druckt ihn auf dem A4-Drucker der Druckstation.
 * Welche Geräte daraufkommen, entscheidet der Server — deshalb wird nur der
 * Empfänger geschickt. Wirft mit der Klartextmeldung des Servers; schlägt der
 * Druck fehl, ist auch nichts gespeichert.
 */
/**
 * Legt den Lieferschein an. `stockDeviceIds` sind die Geräte, die mitgehen —
 * herausgenommene bleiben vorbereitet und kommen mit dem nächsten Lieferschein.
 * Die Liste kann nur weglassen, nicht hinzufügen: der Server filtert gegen die
 * offenen Versände.
 */
export async function createDeliverySlip(
  servicepartnerLocationId: number,
  stockDeviceIds: number[],
): Promise<DeliverySlipResult> {
  const body = await request<DeliverySlipResult>(BASE, {
    method: 'POST',
    body:   JSON.stringify({
      servicepartner_location_id: servicepartnerLocationId,
      stock_device_ids:           stockDeviceIds,
    }),
  });
  if (!body.item) {
    throw new ApiError('Server hat kein item zurückgegeben.', 500, 'INTERNAL_ERROR');
  }
  return body.item;
}

/**
 * Lädt den Lieferschein als PDF. Der Endpunkt braucht ein Bearer-Token, ein
 * einfacher Link würde daher ins Leere laufen — deshalb der Umweg über den Blob.
 */
export async function fetchPdfBlob(slipId: number, direction?: SlipDirection): Promise<Blob> {
  const token = await getAccessToken();
  if (!token) {
    throw new ApiError('Nicht angemeldet.', 401, 'UNAUTHORIZED');
  }

  // Jede Richtung hat ihre eigene Vorlage und deshalb ihren eigenen Endpunkt.
  const url = direction === 'from_partner'
    ? `${window.location.origin}/api/return-slips/${slipId}/pdf`
    : `${BASE}/${slipId}/pdf`;

  let resp: Response;
  try {
    resp = await fetch(url, {
      method:  'GET',
      headers: { 'Authorization': `Bearer ${token}` },
    });
  } catch (e) {
    throw new ApiError(`Netzwerkfehler: ${(e as Error).message}`, 0, 'network');
  }

  if (resp.status === 401) {
    logout('none');
  }
  if (!resp.ok) {
    throw new ApiError(`PDF nicht abrufbar (${resp.status})`, resp.status, 'NOT_FOUND');
  }
  return await resp.blob();
}

/** Speichert das PDF unter „RL-8007.pdf" statt es nur anzuzeigen. */
export async function downloadPdf(
  slip: { id: number; slip_number: string; direction?: SlipDirection },
): Promise<void> {
  const url  = URL.createObjectURL(await fetchPdfBlob(slip.id, slip.direction));
  const link = document.createElement('a');
  link.href     = url;
  link.download = `${slip.slip_number}.pdf`;
  link.click();
  // Erst freigeben, wenn der Browser die Datei übernommen hat.
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}
